import { put, takeEvery, select } from 'redux-saga/effects'

import { sendCtaAction } from '@redux/actions/engage'
import {
  CTA_SUBMIT_FAIL,
  ON_CTA_SUBMIT,
  SET_BREADCRUMB,
  SET_ENGAGEMENT_DETAIL_ID,
  SET_ENGAGEMENTS,
  SET_FILTERED_ENGAGEMENTS,
  SET_REDIRECT
} from '@redux/actions/types'

import { toast } from 'react-toastify'

import { SET_IS_READY } from '../actions/types'

const getAccountId = state => state.auth.accountId

const getEngagements = ({ firestore: { ordered }, auth: { accountId } }) => ordered[`engage/${accountId}/cta`]

const getHash = state => state.auth.hash

const getReadyData = state => state.initialize

const getShouldNavigate = state => state.engage.shouldNavigate

const getSendData = state => ({
  detailId: state.engage.detailId,
  replyGuid: state.engage.replyGuid
})

/**
 * Dispatches a CTA submission action with auth and form data.
 * @param action
 */
function * onCtaSubmit(action) {
  const { auth, data } = action.payload
  yield put(sendCtaAction({ auth, data }))
}

/**
 * Displays a warning toast when a CTA submission fails.
 */
function onCtaSubmitFail() {
  toast.warn('Error submitting tip.')
}

/**
 * Filters top-level engagements from Firestore and dispatches them to the store.
 */
function * onSetEngagements() {
  const engagementsObj = yield select(getEngagements)

  let engagements = []

  if (!engagementsObj) return

  engagements = Object.keys(engagementsObj)
    .map((key) => engagementsObj[key])
    .filter(engagement => engagement.parent_id === null)

  yield put({
    type: SET_FILTERED_ENGAGEMENTS,
    payload: engagements
  })
}

/**
 * Refreshes engagements and handles CTA navigation when an engage document is modified.
 * @param action
 */
function * onDocumentModified(action) {
  const accountId = yield select(getAccountId)

  const isEngage = action.meta.collection === `engage/${accountId}/cta`

  if (!isEngage) return

  yield onSetEngagements()

  const { detailId, replyGuid } = yield select(getSendData)

  if (action.meta.doc === detailId) {
    const { suggestions } = action.payload.data

    const suggestion = suggestions.find(suggestion => suggestion.reply_guid === replyGuid)

    if (!suggestion) return

    const { cta_reference: ctaReference } = suggestion

    const shouldNavigate = yield select(getShouldNavigate)

    if (ctaReference && shouldNavigate) {
      const hash = yield select(getHash)

      const personalAccessCode = hash.substring(0, 10)

      yield put({
        type: SET_REDIRECT,
        payload: `/${personalAccessCode}/engage/${ctaReference}`
      })
    }
  }
}

/**
 * Marks engage as ready when the initial Firestore listener response is received.
 * @param action
 */
function * onListenerResponse(action) {
  const accountId = yield select(getAccountId)

  const isEngage = action.meta.collection === `engage/${accountId}/cta`

  if (!isEngage) return

  const { engageIsReady } = yield select(getReadyData)

  if (engageIsReady) return

  yield put({
    type: SET_IS_READY,
    payload: {
      engageIsReady: true
    }
  })
}

/**
 * Builds and dispatches breadcrumb navigation for a given engagement detail ID.
 * @param root0
 * @param root0.detailId
 */
function * setBreadCrumbForDetailId({ detailId }) {
  const engagementsObj = yield select(getEngagements)

  const engagements = Object.keys(engagementsObj).map(key => engagementsObj[key])

  const detailEngagement = engagements.find(e => e.id === detailId)

  const breadcrumbs = [
    {
      key: detailId,
      to: `/engage/${detailId}`,
      linkText: detailEngagement.message_text
    }
  ]

  const getParentIdFromDetailId = detailId => {
    const engagement = engagements.find(e => e.id === detailId)

    // eslint-disable-next-line camelcase
    const parentId = engagement?.parent_id

    if (parentId) {
      const parentEngagement = engagements.find(e => e.id === parentId)

      breadcrumbs.push({
        key: parentId,
        to: `/engage/${parentId}`,
        linkText: parentEngagement.message_text
      })

      getParentIdFromDetailId(parentId)
    }
  }

  getParentIdFromDetailId(detailId)

  yield put({
    type: SET_BREADCRUMB,
    payload: [
      {
        key: 'breadcrumb-list',
        to: '/engage',
        linkText: 'Open Engagements'
      },
      ...breadcrumbs.reverse()
    ]
  })
}

/**
 * Triggers breadcrumb generation when an engagement detail ID is set.
 * @param action
 */
function * onSetEngagementDetailId(action) {
  const { payload: detailId } = action

  yield setBreadCrumbForDetailId({ detailId })
}

/**
 * Registers watchers for engage-related actions and Firestore events.
 */
function * watchEngage() {
  yield takeEvery(ON_CTA_SUBMIT, onCtaSubmit)
  yield takeEvery(CTA_SUBMIT_FAIL, onCtaSubmitFail)
  yield takeEvery(SET_ENGAGEMENT_DETAIL_ID, onSetEngagementDetailId)
  yield takeEvery(SET_ENGAGEMENTS, onSetEngagements)
  yield takeEvery('@@reduxFirestore/DOCUMENT_MODIFIED', onDocumentModified)
  yield takeEvery('@@reduxFirestore/LISTENER_RESPONSE', onListenerResponse)
}

export default watchEngage
